import { useCallback, useEffect, useRef } from 'react';
import type { Layout } from 'react-grid-layout';

import { APP_SCROLL_CONTAINER_CLASSNAME } from 'utils/constants';

/** Distance from the scroller's top/bottom edge, in px, where auto-scroll kicks in. */
const EDGE_SIZE = 80;
/** Scroll speed, in px per frame, with the pointer at (or past) the edge. */
const MAX_SPEED = 20;

type Bounds = { top: number; bottom: number };

/**
 * Signed px step for the pointer's depth into an edge zone (0 outside them). Downward
 * steps stop at the grid's last row; `getContentBottom` is lazy since it forces a layout.
 */
export function getAutoScrollStep(
  pointerY: number,
  view: Bounds,
  getContentBottom: () => number
): number {
  const edge = Math.min(EDGE_SIZE, (view.bottom - view.top) / 3);
  if (edge <= 0) {
    return 0;
  }
  if (pointerY < view.top + edge) {
    return -Math.round(MAX_SPEED * Math.min(1, (view.top + edge - pointerY) / edge));
  }
  if (pointerY > view.bottom - edge) {
    const speed = Math.round(MAX_SPEED * Math.min(1, (pointerY - (view.bottom - edge)) / edge));
    return Math.max(0, Math.min(speed, Math.floor(getContentBottom() - view.bottom)));
  }
  return 0;
}

/**
 * react-grid-layout doesn't scroll mid-gesture, so this scrolls the app while the
 * mouse is held near an edge, replaying the pointer so the tile follows the scroll.
 */
export function useDragAutoScroll() {
  const frameRef = useRef<number | null>(null);
  const pointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  // The scroller's visible area, measured once per gesture: it doesn't move while a
  // tile is dragged, and measuring it every frame forces a layout mid-drag.
  const viewRef = useRef<Bounds | null>(null);
  const gridRef = useRef<HTMLElement | null>(null);

  const stopAutoScroll = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = null;
    pointerRef.current = null;
    scrollerRef.current = null;
    viewRef.current = null;
    gridRef.current = null;
  }, []);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const step = useCallback(() => {
    frameRef.current = null;
    const scroller = scrollerRef.current;
    const view = viewRef.current;
    const grid = gridRef.current;
    const pointer = pointerRef.current;
    if (!scroller || !view || !grid || !pointer) {
      return;
    }
    const speed = getAutoScrollStep(
      pointer.clientY,
      view,
      () => grid.getBoundingClientRect().bottom
    );
    if (speed === 0) {
      // Idle until the pointer moves back into an edge zone (or the grid grows).
      return;
    }
    // Schedule first: the replayed mousemove re-enters `updateAutoScroll` synchronously.
    frameRef.current = requestAnimationFrame(step);
    const before = scroller.scrollTop;
    scroller.scrollTop = before + speed;
    if (scroller.scrollTop !== before) {
      scroller.ownerDocument.dispatchEvent(
        new MouseEvent('mousemove', { ...pointer, bubbles: true })
      );
    }
  }, []);

  /** Pass as the grid's `onDrag` / `onResize`. */
  const updateAutoScroll = useCallback(
    (
      _layout: Layout[],
      _oldItem: Layout,
      _newItem: Layout,
      _placeholder: Layout,
      event: Event,
      element: HTMLElement
    ) => {
      if (!(event instanceof MouseEvent)) {
        return;
      }
      pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      if (!scrollerRef.current) {
        const doc = element.ownerDocument;
        const scroller =
          element.closest<HTMLElement>(APP_SCROLL_CONTAINER_CLASSNAME) ??
          (doc.scrollingElement as HTMLElement | null);
        if (!scroller) {
          return;
        }
        scrollerRef.current = scroller;
        viewRef.current =
          scroller === doc.scrollingElement
            ? { top: 0, bottom: doc.defaultView?.innerHeight ?? scroller.clientHeight }
            : scroller.getBoundingClientRect();
        // The grid container, sized to the layout's rows. `element` is the tile for a
        // drag but the resize handle for a resize, so look it up rather than assume.
        gridRef.current = element.closest<HTMLElement>('.react-grid-layout');
      }
      const view = viewRef.current;
      // Only start the loop from an edge zone, so ordinary moves cost no frame work.
      if (
        view &&
        frameRef.current === null &&
        getAutoScrollStep(event.clientY, view, () => Infinity) !== 0
      ) {
        frameRef.current = requestAnimationFrame(step);
      }
    },
    [step]
  );

  return { updateAutoScroll, stopAutoScroll };
}
